import { AIRCRAFT } from '../config/constants.js'
import { RAD } from '../util/units.js'

/**
 * The small piece of aerodynamics the on-rails trainer still needs.
 *
 * The aircraft no longer flies itself out of forces and moments — the path is
 * commanded. But the *attitude* it holds along that path is not free. Angle of
 * attack is what the pilot reads on the indexer, it is what sets the pitch
 * attitude you see over the nose, and it is what puts the hook 16.8 ft below
 * the eye on the ball. Guessing it would show up immediately: a Tomcat that
 * flies the groove at 3 degrees nose up looks nothing like one at 10.5.
 *
 * So alpha is *solved*, not scripted. Given speed, weight and load factor, the
 * wing has to produce a specific lift coefficient, and that coefficient implies
 * an alpha. Everything else follows.
 *
 * THE COEFFICIENTS ARE NOT INVENTED. They are a linearisation of the lift
 * curve in `src/fdm/data/aircraft/f-14b/f-14b.xml` around the approach point,
 * which is the same table `npm run verify` uses to prove the jet trims at 15
 * units and 136 knots. `scripts/test-rails.mjs` re-parses that XML and asserts
 * this linear model reproduces it — so if anyone retunes the flight model, the
 * rails follow rather than silently drifting away from it.
 */

/** Wing area, square feet. Straight from <metrics> in f-14b.xml. */
export const WING_AREA_SQFT = 565

/** Sea-level density, slugs per cubic foot. */
export const RHO_SL = 0.0023769

/**
 * Lift-curve slope per radian, from the Lift_alpha table between 0.1 and 0.2
 * rad — the segment the approach sits in. (1.010 - 0.540) / 0.1 = 4.70; the
 * segment below it gives 4.80. 4.75 splits them.
 */
export const CL_ALPHA = 4.75

/**
 * Zero-alpha lift with the wing clean and the stabilator where it trims on
 * approach. The table's own intercept is 0.060; the trimmed stabilator gives
 * back about 0.043, and what is left is this.
 */
export const CL0_CLEAN = 0.0174

/** Lift increment from full flaps and slats. Scalar Lift_flaps in the XML. */
export const CL_FLAP = 0.620

/**
 * Recovery weight, pounds.
 *
 * An F-14B empties out at about 41,800 lb. Back from a cycle with 6,600 lb of
 * fuel, two crew and no stores you are near 49,000 — comfortably under the
 * 51,800 lb maximum arrested landing weight, which is where you want to be
 * when you hit the deck at 12 feet per second.
 *
 * `npm run verify` separately proves the flight model trims on speed at 54,000
 * lb. That is the max-trap check; this is the number you actually land at, and
 * it is 6 knots slower.
 */
export const RECOVERY_WEIGHT_LBS = 49000

/**
 * Indexer units per degree of alpha. Straight from the aoa-units gain in
 * fcs.xml, and the reason 15 units is 10.5 degrees.
 */
export const UNITS_PER_DEG = 1.428571

/** Density ratio. The whole pattern is below 1,000 ft, so this barely moves. */
export function densityRatio(altFt) {
  return Math.pow(1 - 6.87535e-6 * Math.max(altFt, 0), 4.2561)
}

/**
 * Solve for the angle of attack the wing must be at.
 *
 * @param {object} o
 * @param {number} o.ktas      true airspeed, knots
 * @param {number} o.altFt     pressure altitude, feet
 * @param {number} o.weightLbs
 * @param {number} o.nz        load factor normal to the flight path
 * @param {number} o.flapNorm  0 clean, 1 full
 * @returns {number} alpha in degrees
 */
export function solveAlphaDeg({ ktas, altFt = 0, weightLbs = RECOVERY_WEIGHT_LBS, nz = 1, flapNorm = 0 }) {
  const vFps = Math.max(ktas, 40) / 0.5924838
  const q = 0.5 * RHO_SL * densityRatio(altFt) * vFps * vFps
  const clNeeded = (weightLbs * nz) / Math.max(q * WING_AREA_SQFT, 1e-6)
  const cl0 = CL0_CLEAN + CL_FLAP * flapNorm
  // Above about 22 degrees the real curve has rolled over and a linear solve
  // is meaningless. The rails never go there, but returning a plausible number
  // beats returning 40.
  return Math.min(((clNeeded - cl0) / CL_ALPHA) * RAD, 22)
}

/** Indexer units from alpha, clipped the way the real gauge is. */
export function aoaUnits(alphaDeg) {
  return Math.max(-5, Math.min(45, alphaDeg * UNITS_PER_DEG))
}

/**
 * The speed that puts the jet exactly on speed in the landing configuration.
 *
 * Inverted from the same relation rather than typed in as 134, so that a
 * change to the flap coefficient moves the approach speed instead of quietly
 * putting the trainer half a doughnut fast.
 */
export function onSpeedKtas(weightLbs = RECOVERY_WEIGHT_LBS, altFt = 0, nz = 1) {
  const alphaRad = AIRCRAFT.ONSPEED_AOA_UNITS / UNITS_PER_DEG / RAD
  const cl = CL0_CLEAN + CL_FLAP + CL_ALPHA * alphaRad
  const q = (weightLbs * nz) / (cl * WING_AREA_SQFT)
  const vFps = Math.sqrt((2 * q) / (RHO_SL * densityRatio(altFt)))
  return vFps * 0.5924838
}

/* ===================================================================== *
 *  WING SWEEP                                                           *
 * ===================================================================== */

/**
 * The F-14's automatic wing sweep programme.
 *
 * The wings are not a pilot input in normal operation — a computer schedules
 * them against Mach, and the handle is there for the day it fails. That is why
 * NAVAIR fig 8-3 lists "wing sweep 20 degrees" next to the gear rather than
 * with the actions: it is something you CHECK, not something you do.
 *
 * The chart shows 68 degrees on the run-in at 300 to 350 knots, which the Mach
 * schedule alone would not command at 800 ft — you are only at M 0.53 there.
 * It is showing an aeroplane that has just come down from a fast transit with
 * the wings still programming forward, and that is how the trainer starts: at
 * the initial the wings are aft and they run forward as you come up the
 * starboard side.
 */
const SWEEP_SCHEDULE = [
  [0.0, 20],
  [0.7, 20],
  [0.8, 26],
  [0.9, 38],
  [1.0, 50],
  [1.1, 60],
  [1.35, 68],
]

/** Degrees per second the wings actually travel. */
export const SWEEP_RATE_DEG_S = 7.5

/** Where the programmer wants the wings, given Mach. */
export function autoSweepDeg(mach) {
  const t = SWEEP_SCHEDULE
  if (mach <= t[0][0]) return t[0][1]
  if (mach >= t[t.length - 1][0]) return t[t.length - 1][1]
  for (let i = 1; i < t.length; i++) {
    if (mach <= t[i][0]) {
      const [m0, s0] = t[i - 1]
      const [m1, s1] = t[i]
      return s0 + ((s1 - s0) * (mach - m0)) / (m1 - m0)
    }
  }
  return t[t.length - 1][1]
}
